// frontend/src/components/LoadingState.tsx
import React from 'react';

export interface LoadingStateProps {
  message?: string;
  progress?: number;
  showSkeleton?: boolean;
  onCancel?: () => void;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  progress,
  showSkeleton = false,
  onCancel,
}) => {
  if (showSkeleton) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-4">
      {/* Spinner */}
      <div className="relative">
        <div className="w-16 h-16 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div>
        {progress !== undefined && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-semibold text-sky-600">
              {Math.round(progress)}%
            </span>
          </div>
        )}
      </div>

      {/* Message */}
      <div className="text-center space-y-2">
        <p className="text-lg font-medium text-slate-700">{message}</p>
        <p className="text-sm text-slate-500">
          This may take a few moments...
        </p>
      </div>

      {/* Progress bar */}
      {progress !== undefined && (
        <div className="w-64 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-sky-500 transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Cancel button */}
      {onCancel && (
        <button
          onClick={onCancel}
          className="mt-4 px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
      )}
    </div>
  );
};

export const LoadingSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 p-6 animate-pulse">
      {/* Header skeleton */}
      <div className="h-8 bg-slate-200 rounded w-1/3"></div>
      
      {/* Table skeleton */}
      <div className="space-y-3">
        <div className="h-12 bg-slate-200 rounded"></div>
        <div className="h-12 bg-slate-100 rounded"></div>
        <div className="h-12 bg-slate-200 rounded"></div>
        <div className="h-12 bg-slate-100 rounded"></div>
        <div className="h-12 bg-slate-200 rounded"></div>
      </div>

      {/* Diagram skeleton */}
      <div className="mt-8 space-y-3">
        <div className="h-6 bg-slate-200 rounded w-1/4"></div>
        <div className="h-64 bg-slate-100 rounded"></div>
      </div>
    </div>
  );
};

export const ErrorState: React.FC<{
  error: Error;
  onRetry?: () => void;
  onDismiss?: () => void;
}> = ({ error, onRetry, onDismiss }) => {
  const getErrorMessage = (error: Error): { title: string; message: string; suggestion: string } => {
    // Timeout errors
    if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
      return {
        title: 'Request Timeout',
        message: 'The server is taking too long to respond.',
        suggestion: 'Please check your internet connection and try again.',
      };
    }

    // Network errors
    if (error.name === 'NetworkError' || error.message.includes('Network')) {
      return {
        title: 'Connection Error',
        message: 'Unable to connect to the server.',
        suggestion: 'Please check your internet connection or try again later.',
      };
    }

    // API errors
    if (error.name === 'ApiError') {
      return {
        title: 'Server Error',
        message: error.message,
        suggestion: 'Please try again or contact support if the problem persists.',
      };
    }

    // Generic error
    return {
      title: 'Something went wrong',
      message: error.message || 'An unexpected error occurred.',
      suggestion: 'Please try again.',
    };
  };

  const { title, message, suggestion } = getErrorMessage(error);

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-4">
      {/* Error icon */}
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
        <svg
          className="w-8 h-8 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      {/* Error message */}
      <div className="text-center space-y-2 max-w-md">
        <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
        <p className="text-base text-slate-600">{message}</p>
        <p className="text-sm text-slate-500">{suggestion}</p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 mt-4">
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors font-medium"
          >
            Try Again
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

export default LoadingState;
